/**
 * Pure business logic for GPX/GeoJSON import processing.
 *
 * Features are added to the target entity's layer group via
 * featureStorage.createFeature(). For plans, features go directly
 * on the plan. For standalone imports, features go into a layer group.
 */

import type { ImportResult } from '../components/common/import-dialog.js'

// ===== Geometry helpers =====

export function extractAllCoordsFromGeometry(geometry: GeoJSON.Geometry): number[][] {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates]
    case 'LineString':
      return geometry.coordinates
    case 'MultiLineString':
      return geometry.coordinates.flat()
    case 'Polygon':
      return geometry.coordinates.flat()
    case 'MultiPoint':
      return geometry.coordinates
    default:
      return []
  }
}

export function computeBounds(
  coords: number[][]
): { ne: [number, number]; sw: [number, number] } | undefined {
  if (coords.length === 0) {
    return undefined
  }
  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity
  for (const [lng, lat] of coords) {
    if (lng < minLng) {
      minLng = lng
    }
    if (lng > maxLng) {
      maxLng = lng
    }
    if (lat < minLat) {
      minLat = lat
    }
    if (lat > maxLat) {
      maxLat = lat
    }
  }
  return { ne: [maxLat, maxLng], sw: [minLat, minLng] }
}

export function computeLineDistance(geometry: GeoJSON.Geometry): number {
  const R = 6371000 // meters
  const haversine = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }
  const lineLen = (coords: number[][]): number => {
    let d = 0
    for (let i = 1; i < coords.length; i++) {
      d += haversine(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])
    }
    return d
  }
  if (geometry.type === 'LineString') {
    return lineLen(geometry.coordinates)
  }
  if (geometry.type === 'MultiLineString') {
    return geometry.coordinates.reduce((sum, line) => sum + lineLen(line), 0)
  }
  return 0
}

export function computeElevationStats(elevations: number[]): {
  gain: number
  loss: number
  max: number
  min: number
} {
  let gain = 0,
    loss = 0
  for (let i = 1; i < elevations.length; i++) {
    const diff = elevations[i] - elevations[i - 1]
    if (diff > 0) {
      gain += diff
    } else {
      loss += Math.abs(diff)
    }
  }
  return {
    gain: Math.round(gain),
    loss: Math.round(loss),
    max: Math.max(...elevations),
    min: Math.min(...elevations),
  }
}

// ===== Service interfaces for dependency injection =====

export type FeatureType = 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'track'

export interface CreateFeatureData {
  name: string
  description?: string | undefined
  type: FeatureType
  geometry: GeoJSON.Geometry
  properties?: Record<string, unknown> | undefined
  tags?: string[] | undefined
  createdBy: string
}

export interface AddFeatureToEntityOptions {
  entityType: string
  entityId: string
  groupId?: string | undefined
}

export interface FeatureStorageService {
  createFeature(
    data: CreateFeatureData,
    target: AddFeatureToEntityOptions
  ): Promise<{ success: boolean; data?: { id: string } }>
}

export interface PlanStorageService {
  createPlan(data: {
    name: string
    description?: string
    origin: { type: 'imported'; source: string }
    createdBy: string
  }): Promise<{ success: boolean; data?: { id: string } }>
}

export interface ActivityStorageService {
  createActivity(data: {
    name?: string | undefined
    activityType: string
    startTime?: string | undefined
    endTime?: string | undefined
    origin: { type: 'imported'; source: string }
    createdBy: string
  }): Promise<{ success: boolean; data?: { id: string } }>
  linkToPlan(activityId: string, planId: string): Promise<void>
}

export interface LayerGroupManager {
  createLayerGroup(
    name: string,
    properties?: { parentLayerId?: string | undefined }
  ): Promise<{ id: string }>
  importGeoJSONToLayer(geojson: GeoJSON.FeatureCollection, layerId: string): Promise<string[]>
  getLayerGroup(id: string): Promise<{ isSystemGroup?: boolean } | undefined>
  updateLayerGroup(id: string, changes: Record<string, unknown>): Promise<void>
}

export interface ElevationService {
  getElevation(lat: number, lng: number): Promise<number | null>
}

export interface GpxImportDeps {
  featureStorage: FeatureStorageService
  planStorage: PlanStorageService
  activityStorage: ActivityStorageService
  layerGroupManager: LayerGroupManager
  elevationService?: ElevationService | undefined
  userId: string
}

// ===== Result type =====

export interface GpxImportResult {
  planId: string | undefined
  activityId: string | undefined
  featureIds: string[]
  bounds: { ne: [number, number]; sw: [number, number] } | undefined
}

// ===== Geometry type mapping =====

function mapGeometryType(geometryType: string): FeatureType {
  switch (geometryType) {
    case 'Point':
      return 'point'
    case 'LineString':
      return 'line'
    case 'Polygon':
      return 'polygon'
    default:
      return 'point'
  }
}

// ===== DEM elevation helper =====

async function populateDemElevations(
  features: GeoJSON.Feature[],
  elevationService: ElevationService
): Promise<void> {
  for (const feature of features) {
    const geom = feature.geometry
    if (geom.type !== 'LineString' && geom.type !== 'MultiLineString' && geom.type !== 'Point') {
      continue
    }

    const coords = extractAllCoordsFromGeometry(geom)
    if (coords.length === 0) continue

    const demElevations: (number | null)[] = []
    for (const [lng, lat] of coords) {
      demElevations.push(await elevationService.getElevation(lat, lng))
    }

    if (!feature.properties) feature.properties = {}
    feature.properties.coordDemElevations = demElevations
  }
}

// ===== Main processor =====

export async function processGpxImport(
  result: ImportResult,
  deps: GpxImportDeps
): Promise<GpxImportResult> {
  const { featureStorage, planStorage, activityStorage, layerGroupManager, userId } = deps

  let planId: string | undefined
  let activityId: string | undefined
  const allFeatureIds: string[] = []

  // Determine the target plan
  if (result.createPlan && result.newPlanName) {
    const planResult = await planStorage.createPlan({
      name: result.newPlanName,
      origin: { type: 'imported', source: result.metadata.sourceFormat ?? 'gpx' },
      createdBy: userId,
    })
    if (planResult.success && planResult.data) {
      planId = planResult.data.id
    }
  } else if (result.planId) {
    planId = result.planId
  }

  const parsed = result.parsed
  const isGpx = parsed.metadata.sourceFormat === 'gpx'

  // Build the list of features to import as plan/layer features
  const featuresToImport: Array<{
    geoFeature: GeoJSON.Feature
    type: FeatureType
    tags: string[]
  }> = []

  if (isGpx) {
    // GPX: routes from <rte>, or fallback to <trk> geometry (strip times)
    const hasRoutes = parsed.routes.features.length > 0

    if (hasRoutes) {
      for (const f of parsed.routes.features) {
        featuresToImport.push({ geoFeature: f, type: 'line', tags: ['imported'] })
      }
    } else {
      for (const f of parsed.tracks.features) {
        const props = { ...(f.properties ?? {}) }
        delete props.coordTimes
        const stripped = { ...f, properties: props }
        featuresToImport.push({ geoFeature: stripped, type: 'line', tags: ['imported'] })
      }
    }

    // Waypoints
    if (result.importWaypoints) {
      for (const f of parsed.waypoints.features) {
        featuresToImport.push({
          geoFeature: f,
          type: mapGeometryType(f.geometry.type),
          tags: ['imported'],
        })
      }
    }
  } else {
    // KML/GeoJSON: import all features as-is
    for (const f of parsed.allFeatures.features) {
      featuresToImport.push({
        geoFeature: f,
        type: mapGeometryType(f.geometry.type),
        tags: ['imported'],
      })
    }
  }

  // Populate DEM elevations on all features before persisting
  if (deps.elevationService) {
    const allGeoFeatures = featuresToImport.map(f => f.geoFeature)
    if (isGpx && result.createActivity && parsed.metadata.hasTrackTimes) {
      allGeoFeatures.push(...parsed.tracks.features)
    }
    await populateDemElevations(allGeoFeatures, deps.elevationService)
  }

  if (planId) {
    for (const { geoFeature, type, tags } of featuresToImport) {
      const featureResult = await featureStorage.createFeature(
        {
          name: geoFeature.properties?.name ?? result.layerName,
          description: geoFeature.properties?.description,
          type,
          geometry: geoFeature.geometry,
          properties: geoFeature.properties ?? {},
          tags,
          createdBy: userId,
        },
        {
          entityType: 'plan',
          entityId: planId,
        }
      )
      if (featureResult.success && featureResult.data) {
        allFeatureIds.push(featureResult.data.id)
      }
    }
  } else if (featuresToImport.length > 0) {
    // Create a dedicated layer group named after the file so the whole GPX can be
    // toggled/deleted as a unit instead of as loose features. It nests under the
    // layer group the user picked as the import target.
    const importGroup = await layerGroupManager.createLayerGroup(result.layerName || 'Imported', {
      parentLayerId: result.layerGroupId || undefined,
    })
    const geoJsonCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: featuresToImport.map(f => f.geoFeature),
    }
    const ids = await layerGroupManager.importGeoJSONToLayer(geoJsonCollection, importGroup.id)
    allFeatureIds.push(...ids)
  }

  // Activity creation from GPX tracks with times
  if (isGpx && result.createActivity && parsed.metadata.hasTrackTimes) {
    const trackFeatures = parsed.tracks.features.filter(f => {
      const times = f.properties?.coordTimes as unknown[] | undefined
      return times && times.length > 0
    })

    if (trackFeatures.length > 0) {
      const actResult = await activityStorage.createActivity({
        name: parsed.metadata.name ?? result.layerName,
        activityType: 'hike',
        startTime: parsed.metadata.trackStartTime,
        endTime: parsed.metadata.trackEndTime,
        origin: { type: 'imported', source: 'gpx' },
        createdBy: userId,
      })

      if (actResult.success && actResult.data) {
        activityId = actResult.data.id

        for (const f of trackFeatures) {
          const trackResult = await featureStorage.createFeature(
            {
              name: f.properties?.name ?? result.layerName,
              description: f.properties?.description,
              type: 'track',
              geometry: f.geometry,
              properties: f.properties ?? {},
              tags: ['imported'],
              createdBy: userId,
            },
            {
              entityType: 'activity',
              entityId: activityId,
            }
          )
          if (trackResult.success && trackResult.data) {
            allFeatureIds.push(trackResult.data.id)
          }
        }

        if (planId) {
          await activityStorage.linkToPlan(activityId, planId)
        }
      }
    }
  }

  return {
    planId,
    activityId,
    featureIds: allFeatureIds,
    bounds: result.metadata.bounds,
  }
}
